import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import authService from '../services/authService';
import { RootState } from '../store';
import { setCompanyContext, switchCompany } from '../store/slices/authSlice';
import { useAuthState } from '../hooks/useAuthState';

// Define the context shape
interface CompanyContextType {
  currentCompanyId: string | null;
  currentCompanyName: string | null;
  availableCompanies: Array<{ id: string; name: string }> | null;
  isLoading: boolean;
  error: string | null;
  switchCompany: (companyId: string) => Promise<void>;
}

// Create the context with default values
const CompanyContext = createContext<CompanyContextType>({
  currentCompanyId: null,
  currentCompanyName: null,
  availableCompanies: null,
  isLoading: false,
  error: null,
  switchCompany: async () => {
    throw new Error('CompanyContext not initialized');
  },
});

// Provider props
interface CompanyProviderProps {
  children: ReactNode;
}

// Provider component
export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuthState();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get company data from Redux store
  const { companyContext, user: authUser } = useSelector((state: RootState) => state.auth);

  // Find the current company name
  const currentCompanyName = companyContext.availableCompanies?.find(
    company => company.id === companyContext.currentCompanyId
  )?.name || authUser?.companyName || null;

  // Load user's companies on authentication
  useEffect(() => {
    const loadCompanies = async () => {
      if (!isAuthenticated || !user) return;
      // Wait until loginSuccess has been dispatched (sets authUser in Redux).
      // Both Auth0 and Authentik dispatch loginSuccess after the first token exchange.
      if (!authUser) return;

      try {
        setIsLoading(true);
        setError(null);

        // For Authentik: companyId is already in Redux from loginSuccess dispatch.
        // For Auth0: extract from namespace-prefixed token claims.
        let companyId: string | null = companyContext.currentCompanyId;

        if (!companyId) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          companyId = (await authService.getCompanyId(user as any)) || null;
        }

        if (!companyId) {
          console.warn('No company ID found in token');
          setError('No company ID found');
          setIsLoading(false);
          return;
        }

        // Multi-company switching is not used in this deployment — skip the
        // /users/companies/ call and use the current company only.
        dispatch(
          setCompanyContext({
            currentCompanyId: companyId,
            availableCompanies: [{ id: companyId, name: authUser?.companyName || 'Default Company' }],
          })
        );
      } catch (err) {
        console.error('Error loading company data:', err);
        setError('Failed to load company data');
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanies();
  }, [isAuthenticated, user, dispatch, getAccessTokenSilently, authUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle company switching
  const handleSwitchCompany = async (companyId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      await authService.switchCompany(companyId, getAccessTokenSilently);
      dispatch(switchCompany(companyId));
    } catch (err) {
      console.error('Error switching company:', err);
      setError('Failed to switch company');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Context value
  const value: CompanyContextType = {
    currentCompanyId: companyContext.currentCompanyId,
    currentCompanyName,
    availableCompanies: companyContext.availableCompanies,
    isLoading,
    error,
    switchCompany: handleSwitchCompany,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

// Custom hook to use the company context
export const useCompany = () => useContext(CompanyContext);

export default CompanyContext;
